// Standalone passport generator (W2 / W4) — desktop DOC_OPERATOR form that
// posts to /api/v1/admin/passports.  The operator types every field by hand;
// the backend persists the snapshot and enqueues a passport print job (async,
// kind='passport').  The PDF is downloaded later from print history.
//
// Mirrors TestReportForm.  Metrics are entered as a JSON object (same pattern
// as the item-bound operator passport form) — a metric-definition-driven field
// builder is a future polish.

import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import { ErrorBanner } from "@/components/ErrorBanner";
import { PageHeader } from "@/components/PageHeader";
import { RequirePermission } from "@/components/RequirePermission";
import { request } from "@/lib/api";
import { useSystemSettings } from "@/lib/system";

const INPUT = "w-full px-3 py-2 rounded-md bg-surface border border-border text-sm";
const INPUT_MONO = `${INPUT} font-mono`;

interface SavedPassport {
  id: string;
  doc_number: string;
  article: string | null;
  rfid_tag: string | null;
  test_data: Record<string, unknown>;
  performance_metrics: Record<string, unknown> | null;
  meta: Record<string, unknown>;
}

interface CreateResponse {
  passport: SavedPassport;
  print_job: { job_id: string; status: string } | null;
}

interface FormShape {
  doc_number: string;
  article: string;
  rfid_tag: string;
  test_data: string; // JSON, parsed on submit
  performance_metrics: string; // JSON or empty
  doc_date: string;
  customer: string;
  issuer: string;
  notes: string;
}

const DEFAULTS: FormShape = {
  doc_number: "",
  article: "",
  rfid_tag: "",
  test_data: JSON.stringify(
    { salt_rejection: 0.995, permeate_flow_lph: 1100, pressure_bar: 15.5 },
    null,
    2,
  ),
  performance_metrics: "",
  doc_date: new Date().toISOString().slice(0, 10),
  customer: "",
  // Эмитент — из профиля компании (см. эффект ниже).
  issuer: "",
  notes: "",
};

export default function PassportForm() {
  return (
    <RequirePermission codes="document.passport.manage">
      <Inner />
    </RequirePermission>
  );
}

function Inner() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get("from");

  const [f, setF] = useState<FormShape>(DEFAULTS);
  const [hydrated, setHydrated] = useState(!editId);
  const [okMsg, setOkMsg] = useState<string | null>(null);

  const company = useSystemSettings().data?.company;
  useEffect(() => {
    if (company) setF((prev) => (prev.issuer ? prev : { ...prev, issuer: company.name }));
  }, [company]);

  const loaded = useQuery({
    queryKey: ["admin", "passports", "get", editId],
    queryFn: () => request<SavedPassport>(`/admin/passports/${editId}`),
    enabled: !!editId,
  });

  useEffect(() => {
    if (!loaded.data || hydrated) return;
    const p = loaded.data;
    const m = p.meta ?? {};
    const str = (v: unknown) => (typeof v === "string" ? v : "");
    setF({
      doc_number: p.doc_number ?? "",
      article: p.article ?? "",
      rfid_tag: p.rfid_tag ?? "",
      test_data: JSON.stringify(p.test_data ?? {}, null, 2),
      performance_metrics: p.performance_metrics
        ? JSON.stringify(p.performance_metrics, null, 2)
        : "",
      doc_date: str(m.doc_date) || DEFAULTS.doc_date,
      customer: str(m.customer),
      issuer: str(m.issuer) || DEFAULTS.issuer,
      notes: str(m.notes),
    });
    setHydrated(true);
  }, [loaded.data, hydrated]);

  const m = useMutation({
    mutationFn: async (): Promise<string> => {
      let testData: Record<string, unknown>;
      try {
        testData = JSON.parse(f.test_data);
      } catch {
        throw new Error(t("passportForm.errBadMetricsJson"));
      }
      if (testData === null || typeof testData !== "object" || Array.isArray(testData)) {
        throw new Error(t("passportForm.errMetricsObject"));
      }
      let perf: Record<string, unknown> | null = null;
      if (f.performance_metrics.trim()) {
        try {
          perf = JSON.parse(f.performance_metrics);
        } catch {
          throw new Error(t("passportForm.errBadPerfJson"));
        }
      }
      if (!f.doc_number.trim()) throw new Error(t("passportForm.errNoDocNumber"));

      const body = {
        doc_number: f.doc_number.trim(),
        article: f.article.trim() || null,
        rfid_tag: f.rfid_tag.trim() || null,
        test_data: testData,
        performance_metrics: perf,
        meta: {
          doc_date: f.doc_date.trim() || undefined,
          customer: f.customer.trim() || undefined,
          issuer: f.issuer.trim() || undefined,
          notes: f.notes.trim() || undefined,
        },
      };

      if (editId) {
        await request<SavedPassport>(`/admin/passports/${editId}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        const printed = await request<CreateResponse>(`/admin/passports/${editId}/print`, {
          method: "POST",
          body: JSON.stringify({}),
        });
        return printed.print_job?.job_id ?? "";
      }

      const created = await request<CreateResponse>(`/admin/passports`, {
        method: "POST",
        body: JSON.stringify(body),
      });
      return created.print_job?.job_id ?? "";
    },
    onSuccess: (jobId) => {
      setOkMsg(t("passportForm.enqueued", { job: jobId || "—" }));
    },
  });

  function patch<K extends keyof FormShape>(k: K, v: FormShape[K]) {
    setF((cur) => ({ ...cur, [k]: v }));
  }

  if (editId && !hydrated) {
    return <p className="text-text-muted">{t("passportForm.loading")}</p>;
  }

  return (
    <>
      <PageHeader
        eyebrow={t("eyebrows.adminPrint")}
        title={editId ? t("passportForm.titleEdit") : t("passportForm.titleNew")}
        description={editId ? t("passportForm.descEdit") : t("passportForm.descNew")}
      />

      <form
        className="space-y-6"
        onSubmit={(e) => {
          e.preventDefault();
          setOkMsg(null);
          m.mutate();
        }}
      >
        <fieldset className="card p-4 space-y-3">
          <legend className="text-xs font-mono uppercase tracking-wider text-text-muted px-2">
            {t("passportForm.identLegend")}
          </legend>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t("passportForm.docNumber")}>
              <input className={INPUT_MONO} value={f.doc_number} onChange={(e) => patch("doc_number", e.target.value)} />
            </Field>
            <Field label={t("passportForm.article")}>
              <input className={INPUT_MONO} value={f.article} onChange={(e) => patch("article", e.target.value)} />
            </Field>
            <Field label={t("passportForm.rfidTag")}>
              <input className={INPUT_MONO} value={f.rfid_tag} onChange={(e) => patch("rfid_tag", e.target.value)} />
            </Field>
            <Field label={t("passportForm.docDate")}>
              <input type="date" className={INPUT} value={f.doc_date} onChange={(e) => patch("doc_date", e.target.value)} />
            </Field>
          </div>
        </fieldset>

        <fieldset className="card p-4 space-y-3">
          <legend className="text-xs font-mono uppercase tracking-wider text-text-muted px-2">
            {t("passportForm.metricsLegend")}
          </legend>
          <Field label={t("passportForm.metrics")}>
            <textarea
              rows={8}
              className={INPUT_MONO}
              value={f.test_data}
              onChange={(e) => patch("test_data", e.target.value)}
            />
          </Field>
          <p className="text-xs text-text-muted">{t("passportForm.metricsHint")}</p>
          <Field label={t("passportForm.perfMetrics")}>
            <textarea
              rows={4}
              className={INPUT_MONO}
              value={f.performance_metrics}
              onChange={(e) => patch("performance_metrics", e.target.value)}
            />
          </Field>
        </fieldset>

        <fieldset className="card p-4 space-y-3">
          <legend className="text-xs font-mono uppercase tracking-wider text-text-muted px-2">
            {t("passportForm.docLegend")}
          </legend>
          <Field label={t("passportForm.customer")}>
            <textarea rows={3} className={INPUT} value={f.customer} onChange={(e) => patch("customer", e.target.value)} />
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label={t("passportForm.issuer")}>
              <input className={INPUT} value={f.issuer} onChange={(e) => patch("issuer", e.target.value)} />
            </Field>
          </div>
          <Field label={t("passportForm.notes")}>
            <textarea rows={2} className={INPUT} value={f.notes} onChange={(e) => patch("notes", e.target.value)} />
          </Field>
        </fieldset>

        {m.isError && (
          <ErrorBanner onDismiss={() => m.reset()}>
            {(m.error as Error).message}
          </ErrorBanner>
        )}

        {okMsg && (
          <div className="card p-3 border border-accent text-sm flex items-center justify-between gap-4">
            <span>{okMsg}</span>
            <button type="button" className="btn btn-sm" onClick={() => navigate("/admin/print/passports/history")}>
              {t("passportForm.toHistory")}
            </button>
          </div>
        )}

        <div className="flex justify-end">
          <button type="submit" className="btn btn-primary" disabled={m.isPending}>
            {m.isPending
              ? t("common.saving")
              : editId
                ? t("passportForm.saveAndPrint")
                : t("passportForm.createAndPrint")}
          </button>
        </div>
      </form>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-mono uppercase tracking-wider text-text-muted">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
